import EnvironmentService from "service/environment/EnvironmentService";

let environmentService = new EnvironmentService();
let baseAPI = environmentService.getBaseAPI();
let serviceAPI = environmentService.getServiceAPI();
let ccV2API = environmentService.getCcV2API();
let diagnosisV2API = environmentService.getDiagnosisV2API();
let medicineV2API = environmentService.getMedicineV2API();
let addictionV2API = environmentService.getAddictionV2API();
let familyHistoryV2API = environmentService.getFamilyHistoryV2API();
let activityV2API = environmentService.getActivityV2API();
let allergyV2API = environmentService.getAllergyV2API();
let hospitalisationV2API = environmentService.getHospitalisationV2API();
let labInvestigationV2API = environmentService.getLabInvestigationV2API();
let anthropometricV2API = environmentService.getAnthropometricV2API();
let foodPreferencesV2API = environmentService.getFoodPreferencesV2API();
let dailyScheduleV2API = environmentService.getDailyScheduleV2API();
let aiTranscriptV2API = environmentService.getAiTranscriptV2API();

// NOTE: Only the auth-flow endpoints are ported here (same URLs as the expert
// dashboard). Add other sections as more modules are brought over.
const APIConfig = {
  base: baseAPI,
  microService: serviceAPI,

  user: {
    getOTP: baseAPI + "/employees/otp/get",
    getOTPNew: baseAPI + "/employees/otp/v3/get",
    getDoctorOTP: baseAPI + "/doclogin/otp/v3/get",
    new: baseAPI + "/employees/new",
    // Per-id employee lookup (also historically used to fill audit
    // display names — that now happens server-side on the v2 services
    // via each service's ActorNameService, so the frontend no longer
    // fetches monolith profiles from the browser). Kept for other flows.
    get: baseAPI + "/employees/{0}",
    verifyDocCode: baseAPI + "/docprofile/doctor?code={0}",
    verifyOTP: baseAPI + "/employees/otp/v2/verify",
    verifyOTPNew: baseAPI + "/employees/otp/v3/verify",
    verifyDoctorOTP: baseAPI + "/doclogin/otp/v3/verify",
    // MFA step 2 — login is two-factor: SMS OTP, then an email OTP. The
    // SMS-verify step answers with an mfaStatus challenge rather than a session;
    // this exchanges the partialToken + emailed code for the real one. The
    // endpoint lives under /doclogin but serves ALL non-patient roles
    // (doctor/employee/…), so it is shared by both login flows — see
    // bridge-frontend APIConfig `user.verifyEmailOtp`.
    verifyDoctorEmailOtp: baseAPI + "/doclogin/otp/v3/verify-email",
    token: baseAPI + "/employees/token",
    // Must match the flow that minted the session. phr logs in as an EMPLOYEE
    // (who="employee"), so refresh goes to /employees/token — /doclogin/token
    // refreshes doctor sessions and would not accept this one.
    refreshToken: baseAPI + "/employees/token",
  },

  // Appointments. Same endpoints the bridge dashboard uses — see
  // bridge-frontend/src/service/network/APIConfig.js `myPlan.navigo`.
  myPlan: {
    navigo: {
      /* THE /v1/appointment/* ENDPOINTS BELOW ARE ON baseAPI — the gateway
         (EnvironmentConfig.apiUrl) — since 2026-09-30; until then they went
         straight to serviceAPI. Their neighbours in this block (plans, accounts,
         vendors, documents) are other services and stay on serviceAPI.
         scripts/verify/appointment-host.js holds every one of them to it. */
      // Two SEPARATE params — the controller reads them independently
      // (appointment_controller.go:1697 and :1704):
      //   includeDetails        joins patientName, serviceName, accountCode,
      //                         refundStatus … all absent without it.
      //   includeServiceDetails sets serviceDuration (repository/appointment.go:941).
      // Without the second, serviceDuration never arrives and formatTimeSlot can
      // only render a start time — the Time slot column read "5:00 AM" instead of
      // "5:00 - 5:30 AM".
      fetchAppointments:
        baseAPI +
        "/v1/appointment/filter?includeDetails=true&includeServiceDetails=true",
      statusUpdate: baseAPI + "/v1/appointment/status",
      getModes: serviceAPI + "/v1/service/type",

      // Reason master list. AppointmentInfo carries only reasonId — there is no
      // reason name on the wire and includeDetails doesn't join one, so the id
      // has to be mapped client-side. ?status=true filters to active reasons
      // server-side (appointment_controller.go:1791). Returns a bare array.
      reasonsForConsultation: baseAPI + "/v1/appointment/reason?status=true",

      // ---- Create appointment ----
      // Contract verified live against dev1 2026-07-15 as the logged-in doctor;
      // see scratchpad/create-appointment-VERIFIED.md. Do not re-derive these
      // from the local api-appointment-service checkout — it sits on
      // sticky-floater-release, 282 commits behind upstream/development.

      // NB: NOT bridge's `myPlan.navigoDashboard.createAppointment`, which is a
      // same-named key pointing at /api/v2/navigo/appointment — a different
      // endpoint entirely.
      createAppointment: baseAPI + "/v1/appointment",

      // The patient's accounts -> { patientId, accountCodes: [] }.
      getAccountsForPid: serviceAPI + "/v1/patients/account/{0}",

      // keepAllPlans=true returns the whole catalogue for the patient's
      // accounts — 61 plans, EVERY one with status:1, because status is the
      // plan template's, not the patient's enrolment. There is no client-side
      // way to tell which are bookable; /provider/patient is the only gate.
      getPlansForPid: serviceAPI + "/v1/account/plans/patient/{0}?keepAllPlans=true",

      // Plan-scoped services. NOT serviceDashboard.fetchServicesByFilter, which
      // is tenant-wide and returns services the plan doesn't sponsor.
      getServicesForPatient: serviceAPI + "/v1/account/plans/service/patient",

      // Provider + PRICE for a plan/service/mode/patient, and the price
      // authority. phr sends serviceProviderIds:[] — every provider offering
      // it (widened 2026-09-03, as bridge always has); the operator's own row
      // is only pre-selected, and each row's price is what gets charged.
      getServiceProviderForPatient:
        serviceAPI + "/v1/account/plans/service/provider/patient",

      // Slots — and the only authority for the four vendor echo fields.
      getAvailableSlots: baseAPI + "/v1/appointment/available-slots",

      // Wallet/sublimit for a paid booking. Read-only.
      checkCurrentWalletDetails:
        baseAPI + "/v1/appointment/getpaymentsublimit?sublimit=true&wallet=true",

      // Retry a payment link for an already-created appointment.
      generatePaymentLink: baseAPI + "/v1/appointment/payment/initiate",

      // Move an existing appointment to a new date + slot. Bridge's
      // navigo-module reschedule (NavigoDashboardUtils.rescheduleAppointment →
      // POST /v1/appointment/reschedule). Availability is the SAME
      // getAvailableSlots call with isReschedule:true in the body.
      rescheduleAppointment: baseAPI + "/v1/appointment/reschedule",

      /* The rest of bridge's reschedule dialog (navigo-module DocumentUpload.js),
         none of which phr had called before 2026-09-10. NONE of these three
         routes exists in any local branch of api-appointment-service — their
         contracts are known only from bridge's consumer code — so they are
         unverified from this workspace:
           · the reason CHIPS: a bare array of strings, rendered as they come,
             with "Other reason" appended client-side;
           · the ranged slots for the whole 7-day strip in one request:
             { dates: [{ date, slots[], error, serviceProviderId }] };
           · the Language options: a flat array of display names under
             `languages`, echoed back verbatim as `languageMatch`. */
      rescheduleReasons: baseAPI + "/v1/appointment/reschedule-reasons",
      getAvailableSlotsRange: baseAPI + "/v1/appointment/available-slots-range-bridge",
      getProviderLanguages: serviceAPI + "/v1/account/plans/service/provider/language/options",

      /* The booking window for appointments whose window is NOT the chosen
         provider's — bridge's getMinDate / getMaxDate branches. Each answers
         { minBookingDays, maxAdvBookingDays }:
           · avail-care consultations  GET  booking/config/{patientID}/{planID}?program=avail
           · home sample (not vaccine) GET  booking/config/{patientID}/{planID}
           · home-sample vaccine       POST booking/config/vendor
                                            { patientID, serviceID, planID, vendorId }
         and the priority vendors, whose providers use the global home-sample
         window: GET vendor/priority → [{ mappedServiceProviderId }]. */
      getAvailCareDates: baseAPI + "/v1/appointment/booking/config/{0}/{1}?program=avail",
      getHomeSampleDates: baseAPI + "/v1/appointment/booking/config/{0}/{1}",
      getBookingConfigByVendor: baseAPI + "/v1/appointment/booking/config/vendor",
      getPriorityVendors: serviceAPI + "/v1/vendor/priority",

      // Backfill a patient/member's missing DOB or email before booking
      // (bridge's updateProfileInfo). PUT { patientId, firstName, lastName,
      // gender, dateOfBirth:"YYYY-MM-DD", phoneNo:String, email, profileImage,
      // countryCode }.
      updateProfileInfo: serviceAPI + "/v1/patients/profile/update",
      // The patient's WORK email — not on the patient record; it lives in
      // api-client-config-service. Read-only here (the Patient-details
      // dialog); 404 "no work email found for patient" when there is none.
      getWorkEmail: serviceAPI + "/v1/account/patient/work-email/patient/{0}",

      // Documents mapped to ONE appointment (the detail screen's Documents
      // section) — bridge's getAppointmentDocMapping. Responds { documents:[…] }.
      // Distinct from documentApis.getByPatientDoc, which is all-patient.
      getAppointmentDocMapping: baseAPI + "/v1/appointment/document/{0}",

      // Attach an already-uploaded document to an appointment — bridge's
      // mapDocToApp, POSTed as { appointmentId, documentId }. Same path as the
      // GET above but WITHOUT the id segment. Uploading alone does not make a
      // document appear on the appointment; this is the call that does.
      mapDocToApp: baseAPI + "/v1/appointment/document",

      // The cancellation-reason master list — bridge's cancellationReasons.
      // Responds a bare array of strings (server: GetCancellationReasons, an
      // env-driven list). MANDATORY on a cancel: the server writes the chosen
      // text to the appointment's `updateReason` and into the audit trail.
      cancellationReasons: baseAPI + "/v1/appointment/cancellation-reasons",

      // Fire-and-forget: tells the lab-test pipeline a document landed so a
      // report can be auto-digitised. Bridge calls it after every upload and
      // ignores the result. Body { patientId, documentURL }.
      labtestAutomationWebhook: serviceAPI + "/api/v1/labtest-automation-webhook",

      // The status/audit trail for ONE appointment — what the detail screen's
      // "Appointment history" table renders, and the ONLY source of
      // rescheduleCount, which the status strip's "Rescheduled — N times" needs.
      // Bridge's getAppointmentAudit. Responds
      //   { appointmentAudits: [{statusName, updatedOn, updatedBy,
      //                          updatedFrom, comment}], rescheduleCount }
      // `updatedOn` arrives PRE-FORMATTED ("2 Feb 2026, 03:25 PM") — render it
      // raw. Passing it through formatLongDate yields "Invalid Date".
      getAppointmentAudit: baseAPI + "/v1/appointment/audit/{0}",

      // A short-lived (~15 min) signed URL for ONE call recording. Fetched per
      // click rather than per row precisely because it expires — prefetching a
      // table's worth would hand out URLs that are dead before they are used.
      // Responds { url }.
      getCallRecordingSignedUrl:
        serviceAPI + "/v1/documents/recordings/{0}/download-url",

      // Edit the external order id. The NEW VALUE TRAVELS IN THE PATH and the
      // body is null — verified against bridge's NavigoDashboardUtils:219,
      // which PUTs with a null body. Sending it as a body would no-op.
      updateExtOrderId: baseAPI + "/v1/appointment/{0}/eoid/{1}",
      // phr "Update booking for": sets the appointment's own updatedBookingFor field
      // (api-appointment-service). Read back with the appointment itself.
      bookingFor: baseAPI + "/v1/appointment/{0}/booking-for",
    },
    /* The service TYPE and SUBTYPE catalogues, which the referral Specialty
       picker walks to turn the four subtype NAMES into the ids the service
       filter takes (helper/referralSpecialties). Key strings are bridge's
       verbatim (its APIConfig.js:1116-1117), so one loader serves both forks.

       `status` is the string "true"/"false" here, NOT the "active"/"inactive"
       the service filter below takes — api-services-service compares it with
       EqualFold against "true" (controllers/service_type.go:96,
       service_subtype.go:238). Anything else, INCLUDING an empty string, leaves
       the isActive filter nil, i.e. unrestricted. */
    createService: {
      getServiceType: serviceAPI + "/v1/service/type?status={0}",
      getServiceSubType: serviceAPI + "/v1/service/subtype/type/{0}?status={1}",
    },
    serviceDashboard: {
      fetchServicesByFilter: serviceAPI + "/v1/service/service/filter",
    },
    serviceProviderDashboard: {
      fetchServiceProvidersByFilter: serviceAPI + "/v1/service/provider/filter",
    },
    accountDashboard: {
      getAccountsByFilter: serviceAPI + "/v1/account/filter",
    },
  },

  /**
   * Progress notes. Ported verbatim from bridge (`newProgressNote` /
   * `progressNoteComment` / `progressNoteTaskZoho`). This is an Eve-style Mongo
   * REST API, not the plain REST the rest of phr talks to, so the query strings
   * are load-bearing:
   *   where=      a JSON filter object   {"patientId":N} / {"patientId":N,"tag":T}
   *   embedded=   {"comments":1} inlines each note's comments (no N+1)
   *   max_results / page   paging (bridge asks for 15 a page)
   *   sort=[("_id",-1)]    newest first
   *   time=       cache-buster, a plain Date.now()
   * The list responds { items: [...] } — NOT Eve's default `_items`.
   *
   * Mind the {n} slots, they are NOT in call order: getAll takes
   * (patientId, maxResults, page, time) and getAllWithType
   * (patientId, maxResults, page, tag, time). `search` only substitutes {0} and
   * {4} — it filters on patientId + a body regex and ignores tag AND paging.
   * That is bridge's shape; bridge compensates by resetting the active tag to
   * ALL whenever a search runs, so the results never contradict the chips.
   */
  progressNotes: {
    new: serviceAPI + "/api/v1/progress_notes",
    getAll:
      serviceAPI +
      '/api/v1/progress_notes?where={"patientId":{0}}&embedded={"comments":1}&max_results={1}&page={2}&sort=[("_id",-1)]&time={3}',
    getAllWithType:
      serviceAPI +
      '/api/v1/progress_notes?where={"patientId":{0},"tag":{3}}&embedded={"comments":1}&max_results={1}&page={2}&sort=[("_id",-1)]&time={4}',
    update: serviceAPI + "/api/v1/progress_notes/{0}",
    // One note, comments inlined. Lets a card refresh itself after a comment or
    // an acknowledgement without re-pulling (and collapsing to) page 1.
    getOne: serviceAPI + '/api/v1/progress_notes/{0}?embedded={"comments":1}&time={1}',
    search:
      serviceAPI +
      "/api/v1/progress_notes?where={%22patientId%22:{0},%20%22body%22:%20{%22%24regex%22:%20%20%22{4}%22,%20%20%22%24options%22:%20%22i%22}}&embedded={%22comments%22:1}&sort=[(%22_id%22,-1)]",
  },
  progressNoteComment: {
    new: serviceAPI + "/api/v1/comments",
    update: serviceAPI + "/api/v1/comments/{0}",
  },
  /**
   * Tasks attached to a progress note live in Zoho. Same paths as bridge's
   * `progressNoteTaskZoho`.
   *
   * PROD-ONLY, and they 404 on dev1 BY DESIGN — Zoho task management is only
   * exercised in production. dev1's ingress routes /v1/tm (go-task-manager-service)
   * and has no /v2/tm at all, so these three cannot be driven locally. That is
   * expected: do NOT "fix" the dev1 404 by dropping to /v1/tm. Everything else in
   * progress notes is verifiable on dev1; the task panel is the one piece whose
   * wiring is confirmed on prod.
   */
  progressNoteTask: {
    newTask: serviceAPI + "/v2/tm/project/task",
    projects: serviceAPI + "/v2/tm/projects",
    usersByProject: serviceAPI + "/v2/tm/users/{0}",
  },

  /**
   * Health plan — bridge's NEW (v4) flow verbatim (new-bridge/modules/health-plan,
   * HealthPlanService.js + newIndex.js). A patient has numbered plan versions
   * (bridge calls them "levels"; `current_level` is the string "Level N"); one
   * may be published. The plan body is { id, current_level, data, patientId,
   * published, createdBy, updatedBy, updatedByName, publishedBy, publishedById,
   * createdAt, updatedAt } where `data` is keyed by CATEGORY id (from
   * /v4/category) → { items: [...], guidelines: [...] }.
   * There is NO separate publish endpoint — publish is the same create/update
   * call with published:true.
   */
  healthPlan: {
    latest: serviceAPI + "/v4/hp/{0}/latest",
    byId: serviceAPI + "/v4/hp/{0}/{1}",
    // Bridge hardcodes pageSize=2 (its dropdown shows only the last two);
    // phr's history panel wants the real list, and the server honours a
    // larger page.
    list: serviceAPI + "/v4/hp/list/{0}?pageNo=1&pageSize=50",
    create: serviceAPI + "/v4/hp/{0}",
    update: serviceAPI + "/v4/hp/{0}/{1}",
    categories: serviceAPI + "/v4/category",
    itemsByCategory: serviceAPI + "/v4/item/category/{0}",
    optionSearch: serviceAPI + "/v4/option/search/opt/{0}?query={1}",
    optionDetail: serviceAPI + "/v4/option/detail/{0}",
    guidelinesByCategory: serviceAPI + "/v1/guidelines/cat/{0}",
    ingredientUnits: serviceAPI + "/v1/ingredients/units",
    // The ingredient master list behind a recipe's "+ Add ingredient" (bridge's
    // recipeDashboard.ingredient). Responds a bare array of { id, name, category }.
    ingredientsAll: serviceAPI + "/v1/ingredients/all",
    // Auto-generate the nutrition plan ({0}=patientId, {1}=employeeId) — the
    // options it creates come back with auto:true. `?source=v2` opts phr's
    // generate call into hp-automation's v2 tracking + DA fetches (per-
    // request switch on top of the deployment-level env defaults).
    generate: serviceAPI + "/v4/hp/nutrition/{0}/{1}?source=v2",
    // Truthy = migrated to the v4 model; falsy = still on the legacy plan
    // (bridge's newHealthplanStatus check).
    migrationCheck: serviceAPI + "/v1/hpmgrn/check/{0}",
    // The patient's food list ({1}=filter, bridge sends "all") — responds
    // { recommendedFoodList, allowedFoodList, limitedFoodList,
    //   notAllowedFoodList } of { foodItemName, foodItemType, ... }.
    foodList: serviceAPI + "/v1/foodlist/{0}/latest?filter={1}",
    // Kick off PDF generation after publish; body { patientId, healthPlanId,
    // author: { name, employeeId } }. Poll pdfStatus until { pdfURL } appears —
    // bridge gates the publish notifications on the PDF being ready.
    pdfGenerate: serviceAPI + "/v1/hp-pdf",
    pdfStatus: serviceAPI + "/v1/hp-pdf/{0}/{1}",
    // Section templates (legacy FetchPlan, on Exercise / Medical / Meditation):
    // search a category's templates by name → [{ id, name }]; one template →
    // { id, name, category, data: { [categoryId]: [{ id, name, options, time }] } }.
    templateSearch: serviceAPI + "/api/v1/hp/template/search/{0}?query={1}",
    templateById: serviceAPI + "/api/v1/hp/template/{0}",
    // Suggestions for a recipe ingredient's instruction (legacy Recipe's
    // getinstruction) → [{ id, name }].
    instructionSearch: serviceAPI + "/v1/instructions?filter={0}",
  },

  /**
   * Publish notifications — bridge's growthEvents chain, fired only on
   * PRODUCTION once the health-plan PDF is ready. The WhatsApp event is the
   * trigger the messaging infra consumes; the SMS is the 03XXAZ referral
   * feedback flow. The CRM disposition goes out too (tentacle, below), and
   * 02XXAB patients get bridge's CleverTap A/B event (util/cleverTapAB).
   */
  /**
   * The rest of bridge's growthEvents (production, once the PDF is ready): the
   * CRM "tentacle" disposition for the patient, and the member mapping behind
   * bridge's 02XXAB CleverTap A/B event.
   */
  tentacle: {
    disposition: serviceAPI + "/api/v1/web-hook/health-assessment/tentacle",
    fetchDetails: serviceAPI + "/api/v1/web-hook/health-assessment/disposition?phone={0}",
    memberMapping: serviceAPI + "/v1/patients/mapping/{0}",
  },
  whatsapp: {
    event: serviceAPI + "/api/v1/zylawhatsapp/event",
    short: serviceAPI + "/api/v1/zylawhatsapp/short",
  },
  sms: {
    send: serviceAPI + "/api/v1/patient-communication/sms",
  },
  /**
   * Unified call-initiation endpoint on patient-communication service.
   * Routes internally to Aspire (LiveKit) for Nutritionist /
   * Physiotherapist doctors and to the SparkTG line for everyone else.
   * Response is tagged with `vendor` so the frontend can pick the
   * right follow-up UI (open the transcript modal or show the
   * "call initiated" confirm dialog).
   */
  patientCommunication: {
    initiateCall: serviceAPI + "/api/v1/patient-communication/initiate-call",
  },

  documentApis: {
    // ALL of a patient's documents, grouped by type — bridge's getByPatientDoc.
    // Not appointment-scoped; responds [{ type, count, all:[document] }]. Powers
    // the header's "View all" (Figma 2646:3879).
    getByPatientDoc: serviceAPI + "/v1/documents/patients/{0}?groupBy=type",
    tokenGenerate: baseAPI + "/documents/token/generate",

    // Multipart upload of one document — bridge's documentApis.uploadDoc.
    // FormData carries `file`, `fileName`, `patientId`, and `document` as a
    // JSON STRING. Responds with the stored document, whose `id` is then mapped
    // onto the appointment (myPlan.navigo.mapDocToApp).
    uploadDoc: serviceAPI + "/v1/documents",

    /* Re-file a document into another folder — bridge's documentApis.updateType.
       PATCH, body `{ type: int }`, responds with the WHOLE updated document
       (document-service controllers/document.go:405-433 →
       buildDocumentResponse).

       The server owns two things here, so the client must not duplicate them:
       it folds legacy type 4 → 9 itself (:421-423, the same fold phr applies on
       the read path) and it re-derives `typeTitle` from the new type (:425). */
    updateType: serviceAPI + "/v1/documents/{0}/type",

    /* Rename / re-describe — bridge's documentApis.updateNameAndDescription.
       PATCH, body `{ title, description }`, responds with the whole updated
       document.

       BOTH FIELDS EVERY TIME. The handler assigns them unconditionally —
       `doc.Title = req.Title; doc.Description = req.Description`
       (controllers/document.go:343-344) — so this is not a partial PATCH:
       omitting `description` STORES AN EMPTY ONE over whatever was there. */
    updateName: serviceAPI + "/v1/documents/{0}/name",
  },

  /**
   * Primary-therapy assessment — the patient's main health condition, shown as
   * the header condition chip. `primary` answers with an int code
   * ({ primaryTherapy: 2, answer: "2" }); `questions` is the code->name master
   * ({ fields: [{ id: 2, text: "Heart health" }, …] }). Two calls: read the
   * code, map it to a name. On serviceAPI (pa-service), not baseAPI.
   */
  patientAssessment: {
    primaryTherapy: serviceAPI + "/v2/pa/assessments/{0}/primary",
    therapyQuestions: serviceAPI + "/v2/pa/assessments/questions/1",
    /**
     * Set the primary therapy. POST { answer: "<tag>", questionId: "1" } — a
     * GENERIC assessment write, so `questionId` is not optional: 1 is the
     * primary-therapy question. Both values are strings on the wire.
     * Same endpoint bridge's original patient screen has always used
     * (PatientsService.changeTherapy).
     */
    changeTherapy: serviceAPI + "/v2/pa/assessments/{0}",

    /**
     * Expected delivery date — required before a patient can be put on the
     * Pregnancy therapy, which is why it lives beside these.
     *
     * `read` is templated on the patient; `write` is ONE url for both verbs,
     * POST to add and PUT to update. The two bodies are NOT the same shape:
     * add sends a bare "YYYY-MM-DD", update sends it with a "T00:00:00.000Z"
     * suffix. That asymmetry is bridge's (TherapyInputDialog.onHandleSave) and
     * is reproduced deliberately rather than tidied — the endpoint has been
     * fed exactly this for years and nothing here can prove it tolerates the
     * other form.
     */
    deliveryDateRead: serviceAPI + "/v2/pa/assessments/deliverydate/{0}",
    deliveryDateWrite: serviceAPI + "/v2/pa/assessments/deliverydate",

    /**
     * Minor members on the account. POST { patientIds: [id] } -> an array.
     * The New born care therapy needs one to exist; on family-service
     * (/v1/family), not pa-service.
     */
    minorMembers: serviceAPI + "/v1/family/getminormembers",
  },

  /**
   * Risk stratification (bridge's /miscellaneous/risk-stratification module).
   * `history` lists a patient's assessments newest-first ([{ id, triggeredAt,
   * riskLevel }]); the long AI narrative (`riskSummary`) travels ONLY on
   * `detail`. Bridge always sends includePrevious=true, so we do too.
   */
  /**
   * Call notes (bridge's callplan module). A note belongs to a CALL PLAN entry,
   * not to an appointment — which is the whole difficulty here, since phr opens
   * this from an appointment and nothing server-side maps one to the other.
   *
   * Only these four of bridge's nine declared routes have callers in bridge;
   * the rest (`get`, `getAll`, `getAllByCall`, `update`, `getUserId`) are
   * declared and never used, so they are NOT ported as if proven.
   */
  /**
   * Sleep + step tracker — served by api-sleep-step-tracker-service.
   * Same endpoint bridge-frontend hits (`getSteps` in bridge's APIConfig).
   * `from` and `to` are RFC3339 ISO timestamps
   * (`YYYY-MM-DDTHH:mm:ss.sssZ`). Response is
   *   { patientId, steps: { [ISO]: count }, average, streak }
   * where `steps` is a map keyed by the day's UTC midnight (days with
   * no recorded value are simply absent from the map).
   */
  stepTracker: {
    getSteps: serviceAPI + "/api/v1/sleep-step-tracker/steps/{0}?from={1}&to={2}",
  },

  /**
   * Drug schedule — per-patient dose plans. Served by
   * pharmacy-dose-schedule-api (existing sibling repo). Routes live under
   * /api/v1/nv/ on the shared microservice host (services.<env>.zyla.in).
   *
   * Instruction-for-planner is NOT persisted on the DosePlan — it's
   * re-fetched from the drug catalog and matched by drug name at read
   * time. Care manager sees the current guidance rather than a stale
   * snapshot; historical accuracy is deliberately traded for freshness.
   */
  drugSchedule: {
    /**
     * GET → `[{id, drugname, criticalinstruction, status, createdAt, updatedAt}]`.
     * Returns the FULL catalog (no server-side filter); the picker does
     * client-side fuzzy match over `drugname`. `criticalinstruction` is
     * HTML (Semalix's real value carries `<b>` tags — sanitise + render
     * as HTML in the read-only planner-instruction area).
     */
    drugCatalog: serviceAPI + "/api/v1/nv/drug",

    /**
     * GET → array of DosePlan for this patient. `pid` is the query param
     * name the existing controller reads (controllers/drug.go:59).
     * Response shape (verified from prod entry 2026-08-25):
     *   [{ _id, pid, status: "Active"|"Completed"|"Expired",
     *      doses:[{ doseno, dosename, planneddate:"YYYY-MM-DD",
     *               actualdate, nextdosefrequency:{unit,value}, ... }],
     *      createdby, createdAt, updatedAt }]
     * NOTE: status casing is capitalised; nextdosefrequency.value is a
     * STRING not an int; rescheduledate on doses is ignored.
     */
    listByPatient: serviceAPI + "/api/v1/nv/dose/get?pid={0}",

    /**
     * GET → `[{upcomingdate, doseno, dosename, doseplan, lastdate}]` per
     * plan for this patient. Powers the list table's UPCOMING DOSE
     * ("Dose N of M") + NEXT DOSE DATE columns without the frontend
     * having to derive them per row.
     */
    upcomingByPatient: serviceAPI + "/api/v1/nv/dose/upcomingdosedate?pid={0}",

    /** POST → creates a new DosePlan. Body is the full DosePlan struct. */
    create: serviceAPI + "/api/v1/nv/dose/create",

    /**
     * PUT → updates. Handler (controllers/drug.go:76) maps only
     * `pid`, `doses`, `status`, `createdby`, `updatedAt` — send those
     * five keys + the plan's `id`.
     */
    update: serviceAPI + "/api/v1/nv/dose/update",
  },

  /**
   * Address book — alternate contact numbers for a patient (bridge's
   * "PhoneNo" panel). `pan` in the path stands for "patient alternate
   * numbers"; the endpoint is shared with bridge-frontend (deployed).
   * Not scoped under a v2 service — served by the main patient API.
   */
  addressBook: {
    /** GET → `[{id, patientId, type, alternateNumber, countryCode, areaCode, description}]`. */
    list: baseAPI + "/patients/{0}/pan",
    /**
     * POST → 200 on success. Body:
     * `{patientId, countryCode:int, areaCode:"", alternateNumber:string,
     *   type:"mobile", description}`. Mirrors bridge's addNumber payload.
     */
    add:  baseAPI + "/patients/{0}/pan",
    /**
     * PUT → 200 on success. Body:
     * `{id, patientId, type, alternateNumber, countryCode, areaCode, description}`.
     * `{0}` is patientId, `{1}` is the alternate's id.
     */
    update: baseAPI + "/patients/{0}/pan/{1}",
  },

  callNote: {
    /**
     * The patient's call-plan entries in a date window. Verified against
     * api-calls-service (branch `release`), not inferred from another client.
     *
     * `callee` is the PATIENT ID — it hits `patient_id` (repository/call.go:61-63)
     * — and both dates are epoch MILLISECONDS.
     *
     * Answers a BARE ARRAY of `[{id, plan, is_caregiver, duration, due_date,
     * start_time, end_time, doctorId, patientId, created_at, updated_at,
     * callNotes[], todos[]}]` — `c.JSON(http.StatusOK, cc.buildResponse(...))`
     * over `[]models.CallResponse` (controllers/call.go:185, models/req_res.go:18).
     * NOT `{calls: [...]}`, which is what this comment used to claim.
     *
     * Existing notes DO arrive nested, under `callNotes` (camelCase), fetched
     * per call in buildResponse (controllers/call.go:189-201) — so there is
     * genuinely no separate list endpoint to call.
     *
     * Every timestamp here is epoch MILLIS: `datatype.DateTime.MarshalJSON`
     * returns `Time.UnixMilli()` (datatype/datetime.go:50-55), or a literal
     * `null` for the zero value. Do NOT follow this to `datatype/date.go:38-43`
     * — the sibling `Date` type marshals `Time.Unix()`, i.e. SECONDS. Two epoch
     * conventions live in this service; the call's columns are all `DateTime`.
     *
     * Note `created_at` and `updated_at` are SNAKE case on a note, while the
     * call's own `doctorId` and `patientId` are camel — the entity mixes both.
     * (bridge's ops screen reads `updatedAt` and gets undefined; we render no
     * note timestamp, so it does not bite here.)
     *
     * The server orders `due_date DESC` (repository/call.go:75); we re-sort by
     * nearness to the appointment.
     */
    callPlanByCallee:
      baseAPI + "/calls/callplan/callee?callee={0}&fromDate={1}&toDate={2}",
    /** POST `{ call_id, content }` — snake_case on the wire, content is HTML. */
    new: baseAPI + "/calls/notes/new",
    /**
     * PUT. The body is IGNORED — `Disable` never binds one
     * (controllers/call_notes.go:86-104): it re-reads the row by path id, sets
     * `Disabled = true` and saves, answering 200 with the whole note. So it is
     * idempotent, which is why the caller can fire and forget.
     *
     * Bridge fires this the moment a note reaches the chat. Irreversible from
     * any UI: `BitBool.Value()` returns 1 for false as well as true
     * (datatype/bool.go:38-43), so `disabled` cannot be cleared by this service.
     */
    disable: baseAPI + "/calls/notes/disable/{0}",
    /**
     * Push a SAVED note into the patient's chat. Body is
     * `{ message: "<noteId>", action: "call_note" }` — the note ID as a STRING,
     * never the text. `{0}` is the patientId (bridge's param is named `userId`
     * but every caller passes a patient id).
     */
    directMessage: serviceAPI + "/v1/chat/users/patients/{0}/message",
  },

  /**
   * Call notes, v2 — Mongo, keyed on (patientId, appointmentId).
   *
   * WHY THERE IS A v2. A v1 note hangs off a CALL PLAN row, and a call plan is
   * authored by hand on the ops Call Plan screen. Nothing on the wire links one
   * to an appointment, so this app had to guess the link by date and disabled
   * the whole modal when it missed — which, since no appointment creates a call
   * plan, was most of the time. v2 is keyed on the two ids every appointment
   * screen already has, so there is nothing left to guess and no call picker.
   *
   * v1 above is untouched and still serves bridge, doctor-dashboard and
   * expert-dashboard. phr is the only v2 client.
   *
   * IDS: a NUMBER for notes created from 2026-10-14 (1,000,000,001 and up,
   * from api-calls-service's call_notes_v2 counter) and a 24-char hex ObjectID
   * for the ones before; the API answers both as text. The number is what lets
   * the RELEASED patient app open a note from the chat: it resolves every
   * call-note card through the v1 route `/calls/notes/details/{id}`, which
   * answers a numeric v2 id in the v1 shape and with that same id. A note sent
   * with an ObjectID stays blank there; only a build that knows
   * `/v2/calls/notes/details/{id}` shows it.
   */
  callNoteV2: {
    /** POST `{ patientId, appointmentId, doctorId, content }`. Answers the
     *  saved note, including its `id`. Unlike v1 this validates: a missing
     *  patient, appointment or content is a 400, not a stored orphan. */
    new: baseAPI + "/v2/calls/notes/new",
    /** The notes on one appointment, newest first. Answers a BARE ARRAY —
     *  `[]` when there are none, never null. Both ids are required: the patient
     *  is the security boundary, the appointment only the selector. */
    byAppointment:
      baseAPI + "/v2/calls/notes/patient/{0}/appointment/{1}",
    /** PUT, body ignored. Idempotent, and unlike v1 it is a targeted $set
     *  rather than a whole-document overwrite. */
    disable: baseAPI + "/v2/calls/notes/disable/{0}",
  },

  riskStratification: {
    history: serviceAPI + "/api/v1/risk/history/{0}",
    /**
     * Trigger a fresh stratification for a patient, attributed to a doctor.
     * A GET that MUTATES — it queues a new assessment rather than reading one —
     * so it is only ever fired from an explicit click, never from an effect.
     * `{1}` is the logged-in doctorProfile.id.
     */
    stratify: serviceAPI + "/api/v1/risk/stratify/{0}/{1}",
    detail: serviceAPI + "/api/v1/risk/detail/{0}?includePrevious=true",
  },

  /**
   * Prescription (Create Rx). These are the SAME backend endpoints bridge's
   * /prescription/{patientId} screen uses — not a bridge-only API — so phr calls
   * them directly; the employee login already sends the `bridge_web` client
   * header bridge sends. Everything is keyed on PATIENT id: the payload has no
   * appointment linkage at all (phr resolves the patient from :appointmentId).
   *
   * `create` and `update` are BOTH the draft-and-publish call — `status:false`
   * saves a draft, `status:true` publishes. The email/SMS/WhatsApp switches ride
   * in the QUERY STRING (the backend fans them out), and the PDF url comes back
   * in the save response — there is no generate-then-poll step.
   */
  prescription: {
    draft: serviceAPI + "/api/v1/prescription/draft/{0}",
    latest: serviceAPI + "/api/v1/prescription/{0}",
    create: serviceAPI + "/api/v1/prescription/create",
    update: serviceAPI + "/api/v1/prescription/update",
    // Each published prescription's id, PDF document id (null without one) and doctor,
    // [{id, pdfId, doctorDetails: {name}}], newest first; ?hasPdf=true keeps only those with a
    // PDF — the name beside a prescription in the documents lists. {0}=patient id.
    doctors: serviceAPI + "/api/v1/prescription/doctors/{0}",
    // The patient's measured vitals — height / weight / BMI / weigh-in date.
    // We use ONLY the `PersonalDetails` half of this response, to prefill the
    // Patient-details card. NOTE `dateOfWeightRecorded` is no longer sourced
    // from here: it rides with the weight from the Anthropometric section, and
    // buildSaveBody defaults it for a patient with no reading. It is still
    // `required` on the wire, so it must never be sent null.
    // Its `Reports` half is deliberately IGNORED — the clinical cards come from
    // phr's own APIs, not bridge's tracking service.
    // Swap this for phr's vitals endpoint when it exists. {1}=employee id,
    // {2}=employee name.
    personalDetails:
      serviceAPI + "/api/v1/prescription/tracking/rpd/{0}?userId={1}&userName={2}",
  },

  /**
   * The legacy Rx screen's publish events (prescription/helper/rxPublishEvents):
   * its event log (START_PRESCRIPTION / ERROR_PRESCRIPTION_* /
   * PUBLISH_PRESCRIPTION), and the patient's plan assignments, which its
   * CleverTap RX_PUBLISH event carries. Same paths as bridge's APIConfig
   * (`events`, `planAssignment`). `serviceUrl` and `serviceNew` are one host in
   * every environment, so serviceAPI stands in for bridge's serviceAPINew.
   */
  events: {
    log: serviceAPI + "/v1/events",
  },
  planAssignment: {
    fetchAllPlan: serviceAPI + "/v1/plans/assignments/patients/{0}?active=all",
  },

  /**
   * Family plan — the "Booking for" dependent/member picker on Create
   * appointment (bridge familyPlan). `members` lists a patient's family
   * ({ families: [{ memberPatientId, firstName, lastName, gender, dateOfBirth,
   * relation }] }); the patient themself is prepended client-side as relation 0
   * (Self). `relations` is the code→label master ([{ code, value, gender }]).
   */
  // phr (Orbit) clinician consent on the login terms step — api-consent-service
  // (branch phr). The content read is open (it is shown before the session
  // exists); accept and status carry the clinician's token.
  phrConsent: {
    content: serviceAPI + "/api/v1/consent/phr/content/{0}",
    accept: serviceAPI + "/api/v1/consent/phr/accept",
    status: serviceAPI + "/api/v1/consent/phr/status/{0}",
  },
  familyPlan: {
    members: serviceAPI + "/v1/family/{0}?includeExtended=true",
    relations: serviceAPI + "/v1/family/relations",
  },

  /**
   * Chief Complaints v2 (api-cc-v2-service). Section-by-section revamp — this is
   * the first of the v2 fleet. See project_cc_v2_service memory for the merge
   * cases (add / reactivate via ICD dedup / update-status auto-resolve).
   *
   * Every mutating endpoint takes `expectedVersion` in the body for optimistic
   * locking; a 409 comes back with the fresh doc so the caller can reconcile.
   */
  chiefComplaintsV2: {
    list:                  ccV2API + "/api/v1/complaints/{0}",
    add:                   ccV2API + "/api/v1/complaints/{0}",
    updateStatus:          ccV2API + "/api/v1/complaints/{0}/{1}/status",
    delete:                ccV2API + "/api/v1/complaints/{0}/{1}",
    trend:                 ccV2API + "/api/v1/complaints/{0}/{1}/trend",
    markHistoryIncorrect:  ccV2API + "/api/v1/complaints/{0}/{1}/history/{2}/incorrect",
    correctHistory:        ccV2API + "/api/v1/complaints/{0}/{1}/history/{2}/correct",
  },

  /**
   * ICD-10 catalog search (api-icd-service). Backs the fuzzy-search dropdown in
   * the Chief complaints editor. Hits the services subdomain (NOT the api one
   * — the api subdomain 404s on this path). Response: CodeValuePair[].
   */
  icd: {
    // `searchRanked` returns hits ordered by match quality (exact →
    // starting → word → rest) against disease_chief_complaint /
    // common_terms / code, alphabetical inside each bucket. Backs the
    // four IcdAutocomplete callers: Chief complaints, Diagnosis,
    // Hospitalisation, Family history. `limit` is deliberately omitted
    // — the endpoint returns everything by default; add &limit=N if a
    // caller ever wants a cap.
    search: serviceAPI + "/api/v1/dt/icd/searchRanked?queryString={0}",
  },

  /**
   * Lab-test catalogue (dtlabtest, behind the services host like ICD).
   * Backs Create Rx's "Advised investigations" — the SAME two endpoints bridge's
   * card uses, so a test advised from phr carries the code a bridge-authored
   * prescription would.
   *
   *  search → [{ label: "Beta 2 Microglobulin, CSF (mass/ volume)",
   *              value: "LABT260" }]      (panel-level codes)
   *  info   → the catalogue row for one code; only `.instruction` is read, as
   *           the default advice to prefill when a test is picked.
   *
   * NOT api-lab-investigation-v2-service's `/labtest-catalog`: that returns
   * SUB-test `sltCode`s, a different code space no prescription stores.
   */
  labTest: {
    search: serviceAPI + "/api/v1/dt/labtest/search?queryString={0}",
    /** A panel's sub-tests MATCHING a query — `{0}` is the panel's LABT code. */
    subTests: serviceAPI + "/api/v1/dt/labtest/{0}/sublabtests/search?queryString={1}",
    /**
     * A panel's FULL child list — the same route without `/search`, which takes
     * no queryString at all. LABT31 answers 28 rows.
     *
     * The `/search` sibling's empty-query 400 says "Ltcode is null", which is a
     * copy-paste of its missing-path-param message; that wording is why this
     * route was long assumed not to exist. It does, and it is what makes an
     * honest "Panel · 28 tests" possible. Empty answers 204 with NO BODY.
     */
    children: serviceAPI + "/api/v1/dt/labtest/{0}/sublabtests",
    /**
     * Children for MANY panels in one round-trip — POST `{ltCodes:[…]}`, answers
     * `[{ltCode, testName, subtest:[{sltCode, subTestName, …}]}]`. Replaces a
     * per-panel fan-out. Note the nested key is `sltCode` (camel) where the
     * single-panel route spells it `sltcode`.
     */
    childrenBulk: serviceAPI + "/api/v1/dt/labtest",
    /**
     * Global sub-test search by CANONICAL name, unscoped to a panel. Its
     * projection drops the parent (`LtCode` is in the DAO's hands and thrown
     * away before serialising), so rows arrive `{label, value}` only — pair it
     * with the synonym map / bulk resolve for panel + unit.
     */
    subTestByName: serviceAPI + "/api/v1/dt/labtest/search/subtest/name?queryString={0}",
    info: serviceAPI + "/api/v1/dt/labtest/getby/{0}",
    /**
     * The whole catalog with every panel + its subtests in ONE response.
     * Used by the Digitise-manually modal to load a picker corpus once on
     * open (dropdown + fuzzy search fully client-side; no per-keystroke
     * server round-trips). Response shape:
     *   [{ id, testName, loincCode, ltCode, abbreviatedName,
     *      subtest: [{ sltCode, subtestName, loincCode, abbreviatedName }] | null }]
     */
    withSubTestGetAll: serviceAPI + "/api/v1/dt/labtestwithsubtest/getall",
    /**
     * Single sub-test detail — the ONE endpoint that carries the
     * canonical `loincName` (the readable long-form LOINC label). The
     * getall + subtest-search endpoints only surface `loincCode`, so
     * every Digitise-manually sub-test pick fires this to fill in the
     * LOINC name display. `{0}` is the parent panel's ltCode,
     * `{1}` is the sub-test's sltCode.
     */
    subTestOnSelect: serviceAPI + "/api/v1/dt/labtest/{0}/sublabtests/onselect/{1}",
  },

  /**
   * The lab-test SYNONYM map (api-labtest-service, Mongo side collection —
   * a DIFFERENT store from the MySQL catalogue above).
   *
   * It is the only place two things exist:
   *  - the REVERSE LINK. A row carries `mainTestCode`/`mainTestName`, so
   *    searching "hem" surfaces Hemoglobin *and* the 14 panels containing it —
   *    which the catalogue's own sub-test search cannot do (it drops the parent
   *    before serialising).
   *  - the AUTHORITATIVE UNIT. `unitRef` is the REFLD code itself, so a result's
   *    unit no longer has to be recovered by fuzzy-matching its label.
   *
   * Coverage is PARTIAL BY CONSTRUCTION: rows are hand-seeded and the service
   * refuses to create a synonym unless a unit already exists for that test. Use
   * it to enrich, never as the search of record. Empty answers 200 with body
   * `null` (not 204, and not `[]`). `isLabTest` defaults to false server-side —
   * pass it explicitly. Panels never carry a unit: every write hardcodes
   * `isLabTest:false`.
   */
  labTestMapping: {
    search: serviceAPI + "/api/v1/dt/labtestmapping/search?queryString={0}&isLabTest=false",
    bySubTest: serviceAPI + "/api/v1/dt/labtestmapping/getby/sltcode/{0}",
  },

  /**
   * Reference lists (api-reference-list-service). One call returns EVERY list
   * keyed by name: `{ MEDICINE_FORMULATIONS: [{label,value}], ... }`, where
   * `value` is the REFLD code.
   *
   * Create Rx needs it because api-prescription-service validates each medicine
   * on `formulation.label` (400 "Select a formulation for X") and its PDF
   * renders `formulation.label + MEDICINE NAME` — while medicine v2 stores only
   * the REFLD *code*. This is the code -> label table that bridges the two.
   */
  referenceList: {
    getAllDetails: serviceAPI + "/api/v1/dt/referenceList/getAllDetails",
    /**
     * Fetch a single named reference list. `{0}` is the list name
     * (e.g. `LAB_TEST_UOM` for lab-test units of measurement).
     * Response is a plain array of `{label, value}` where label is the
     * human-readable text and value is the REFLD code.
     */
    getByName: serviceAPI + "/api/v1/dt/referenceList/getByName?name={0}",
  },

  /**
   * Diagnosis v2 (api-diagnosis-v2-service). Second v2 service — same merge
   * cases as CC (add / reactivate via ICD dedup / update-status / delete). Only
   * differences from CC: `ongoing bool` replaces the VAS numeric scale, and
   * each diagnosis carries linked `complaintIds` (real refs to CC v2 entries).
   *
   * Every mutating endpoint takes `expectedVersion` in the body for optimistic
   * locking; a 409 comes back with the fresh doc so the caller can reconcile.
   */
  diagnosisV2: {
    list: diagnosisV2API + "/api/v1/diagnoses/{0}",
    add: diagnosisV2API + "/api/v1/diagnoses/{0}",
    updateStatus: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}/status",
    delete: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}",
    trend: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}/trend",
    markHistoryIncorrect: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}/history/{2}/incorrect",
    correctHistory: diagnosisV2API + "/api/v1/diagnoses/{0}/{1}/history/{2}/correct",
  },

  /**
   * Medicine v2 (api-medicine-v2-service). Third v2 service — same merge
   * pattern as CC + Diagnosis but with a three-state status (ongoing /
   * completed / discontinued) and a richer prescription-shaped state
   * (dose / frequency / duration / instructions). Dedup key is
   * `medicineCode.mCode` from the catalog.
   */
  /* ---------- legacy medication tracking (the "Old" medicines view) ----------
     The same endpoints bridge's Tracking 2.0 panel reads
     (bridge APIConfig.tracking.medicine). READ-ONLY here: the `add` key that
     block also carries is deliberately not restated, so this view cannot write
     even by accident. Same serviceAPI host in all three environments. */
  tracking: {
    medicine: {
      count: serviceAPI + "/api/v1/tracking/md/{0}/count",
      list: serviceAPI + "/api/v1/tracking/md/{0}?pageSize={1}&pageNo={2}",
    },
  },
  /* The salt unit-of-measure master, keyed by `code` — NOT `value` like the
     reference lists. getSaltTherapy resolves each composition's `uom` here. */
  medicineSalt: serviceAPI + "/api/v1/dt/medicines/saltUom",

  medicineV2: {
    list: medicineV2API + "/api/v1/medicines/{0}",
    add: medicineV2API + "/api/v1/medicines/{0}",
    updateStatus: medicineV2API + "/api/v1/medicines/{0}/{1}/status",
    delete: medicineV2API + "/api/v1/medicines/{0}/{1}",
    trend: medicineV2API + "/api/v1/medicines/{0}/{1}/trend",
    adherenceReview: medicineV2API + "/api/v1/medicines/{0}/{1}/adherence",
  },

  /**
   * Medicine catalog (api-medicine-service). Fuzzy-search endpoint used by
   * the Medicine v2 add/edit form to pick a drug. Returns
   *   { data: [{ id, mCode, medicineName, manufacturerName, formulation, ... }],
   *     total, page, limit }
   * — the frontend snapshots the picked entry into `medicineCode` on the
   * medicine record. Hits the services subdomain (same host as ICD search).
   */
  medicineCatalog: {
    // `sort=matchRank` opts this caller into api-medicine-service's
    // match-quality ranked search (exact → starting → word → rest,
    // alphabetical inside each bucket). Server-side hard cap 2000 so
    // limit is set high enough to expose the whole catalog for the
    // ranker to sort — other callers that don't send matchRank keep
    // their existing dateCreated-DESC behaviour untouched.
    search: serviceAPI + "/api/v1/medicine?search={0}&sort=matchRank&limit=2000",
    // Formulation catalog (api-medicine-service). Small enumerated set
    // (~125 rows) — one-shot list drives the medicine-form pickers
    // (label + dose-quantity units + instructions in one shot).
    formulations:      serviceAPI + "/api/v1/medicine/formulations",
    formulationByCode: serviceAPI + "/api/v1/medicine/formulations/{0}",
  },

  /**
   * Addiction v2 (api-addiction-v2-service). Fourth v2 service — different
   * shape from CC / Diagnosis / Medicine: the entry set is fixed 4 (Smoking /
   * Alcohol / Drugs / Tobacco) so there's no add-new-entry endpoint, and no
   * user-facing trend (audit is kept server-side for compliance only). Two
   * POSTs: Add-flow for the full detail write, Update-flow for status changes.
   */
  addictionV2: {
    list:                 addictionV2API + "/api/v1/addictions/{0}",
    add:                  addictionV2API + "/api/v1/addictions/{0}/{1}",
    updateStatus:         addictionV2API + "/api/v1/addictions/{0}/{1}/status",
    trend:                addictionV2API + "/api/v1/addictions/{0}/{1}/trend",
    markHistoryIncorrect: addictionV2API + "/api/v1/addictions/{0}/{1}/history/{2}/incorrect",
  },

  /**
   * Family History v2 (api-family-history-v2-service). Fifth v2 service —
   * user-appended relatives list with full CRUD. No status / no trend
   * (relatives are static records). The Update endpoint is a full-replace
   * of the mutable fields; server preserves AddedBy/AddedAt.
   */
  familyHistoryV2: {
    list: familyHistoryV2API + "/api/v1/family-history/{0}",
    add: familyHistoryV2API + "/api/v1/family-history/{0}",
    update: familyHistoryV2API + "/api/v1/family-history/{0}/{1}",
    delete: familyHistoryV2API + "/api/v1/family-history/{0}/{1}",
  },

  /**
   * Physical Activity v2 (api-activity-v2-service). Sixth v2 service.
   * Two POSTs — full-detail Update (edit-in-place) and status-only Update
   * (used by the status chip modal). Delete is soft.
   */
  activityV2: {
    list:                  activityV2API + "/api/v1/activities/{0}",
    add:                   activityV2API + "/api/v1/activities/{0}",
    update:                activityV2API + "/api/v1/activities/{0}/{1}",
    updateStatus:          activityV2API + "/api/v1/activities/{0}/{1}/status",
    delete:                activityV2API + "/api/v1/activities/{0}/{1}",
    trend:                 activityV2API + "/api/v1/activities/{0}/{1}/trend",
    markHistoryIncorrect:  activityV2API + "/api/v1/activities/{0}/{1}/history/{2}/incorrect",
  },

  /**
   * Activity catalog — fuzzy-search endpoint served by the same service. The
   * frontend picker calls this on debounced input and snapshots the picked
   * entry (name + category) into the Activity record on the patient's doc.
   * Returns { data: [{ id, name, category }] }.
   */
  activityCatalog: {
    search: activityV2API + "/api/v1/activity-catalog?search={0}&limit=20",
  },

  /**
   * Allergy v2 (api-allergy-v2-service). Seventh v2 service. Full CRUD with
   * Allergen field locked in Edit (server preserves identity fields even if
   * a client sends them). No status/trend endpoints.
   */
  allergyV2: {
    list:                  allergyV2API + "/api/v1/allergies/{0}",
    add:                   allergyV2API + "/api/v1/allergies/{0}",
    update:                allergyV2API + "/api/v1/allergies/{0}/{1}",
    delete:                allergyV2API + "/api/v1/allergies/{0}/{1}",
    trend:                 allergyV2API + "/api/v1/allergies/{0}/{1}/trend",
    markHistoryIncorrect:  allergyV2API + "/api/v1/allergies/{0}/{1}/history/{2}/incorrect",
  },

  /**
   * Allergen catalog — federated fuzzy-search served by api-allergy-v2-service.
   * The service fans out to api-medicine-service (drugs) + own allergenCatalog
   * (foods/other) and returns a unified list:
   *   { data: [{ id, name, category, mCode?, source }] }
   * source ∈ "medicine" | "catalog" | "free-text".
   */
  allergenCatalog: {
    search: allergyV2API + "/api/v1/allergen-catalog?search={0}&limit=100",
  },

  /**
   * Reactions catalog — every active row in one shot; the frontend caches
   * the response module-level and derives the "Suggested" chip rail
   * (isCommon=true) + the searchable dropdown from the same payload.
   *   { data: [{ id, reaction, isActive, isCommon }] }
   */
  reactionsCatalog: {
    list: allergyV2API + "/api/v1/reactions-catalog",
  },

  /**
   * Allergen static config — authoritative Category enum. Fetched once
   * by AllergyCategoriesService.js and cached at module scope. Backed
   * by api-allergy-v2-service's constants.AllCategories so widening
   * the taxonomy is a one-place change on the backend.
   *   { categories: string[] }
   */
  allergenConfig: {
    get: allergyV2API + "/api/v1/allergen-config",
  },
  /**
   * Prescription verification — legacy's "Verify Patient" record
   * (api-user-service GET|PUT /patients/{id}/verify, api/patient.go:1093-1172).
   * GET -> { status, comment, doctor } and 404s while the patient has no
   * verification record; PUT takes { status, commentType } and needs one.
   */
  prescriptionVerification: {
    get: baseAPI + "/patients/{0}/verify",
    update: baseAPI + "/patients/{0}/verify",
  },

  /**
   * Hospitalisation v2 (api-hospitalisation-v2-service). Eighth v2 service.
   * Full CRUD with cause (ICD-snapshot; frontend uses IcdAutocomplete against
   * api-icd-service directly) and multi-procedure (federated via the service's
   * own /procedure-catalog proxy).
   */
  hospitalisationV2: {
    list: hospitalisationV2API + "/api/v1/hospitalisations/{0}",
    add: hospitalisationV2API + "/api/v1/hospitalisations/{0}",
    update: hospitalisationV2API + "/api/v1/hospitalisations/{0}/{1}",
    delete: hospitalisationV2API + "/api/v1/hospitalisations/{0}/{1}",
  },

  /**
   * Procedure catalog — pure proxy served by api-hospitalisation-v2-service to
   * the upstream api-hospitalisation-service /surgeries endpoint. Same
   * envelope as the other v2 catalog clients: `{data: [{code, name}]}`.
   * Uses `query` (not `search`) to match the upstream contract.
   */
  procedureCatalog: {
    search: hospitalisationV2API + "/api/v1/procedure-catalog?query={0}",
    all: hospitalisationV2API + "/api/v1/procedure-catalog/all",
  },

  /**
   * Lab investigation v2 (api-lab-investigation-v2-service). Ninth v2 service.
   * Full CRUD + batch endpoint for digitisation results. Server flags each
   * row from ref-range on save.
   */
  labInvestigationV2: {
    list: labInvestigationV2API + "/api/v1/lab-investigations/{0}",
    batch: labInvestigationV2API + "/api/v1/lab-investigations/{0}/batch",
    update: labInvestigationV2API + "/api/v1/lab-investigations/{0}/{1}",
    delete: labInvestigationV2API + "/api/v1/lab-investigations/{0}/{1}",
    // Correction audit flow — same shape as Dx v2's /history/:index/{incorrect,correct}.
    markIncorrect:  labInvestigationV2API + "/api/v1/lab-investigations/{0}/{1}/history/{2}/incorrect",
    correctHistory: labInvestigationV2API + "/api/v1/lab-investigations/{0}/{1}/history/{2}/correct",
    // SLT-code → qualifier-tag catalog (seeded server-side via mongoimport).
    // Frontend caches the response module-level and gates SubTestCard's Tag
    // dropdown on whether the picked sub-test's sltCode has an entry here.
    subtestTags:    labInvestigationV2API + "/api/v1/lab-investigations/subtest-tags",
  },

  /**
   * Digitisation — called DIRECT against the services host (same pattern
   * bridge-frontend uses; no v2 proxy). Response shapes:
   *
   *   documentsForDigitisation → `[{document: {id, title, url, thumbnail,
   *     format, typeTitle, createdAt, updatedAt, ...}, appointment, service}, …]`
   *     Frontend flattens `document.*` into a picker-friendly `{id, name,
   *     url, thumbnail, ...}` for the modal.
   *
   *   triggerDigitisation → POST `{documentId: N}`. Digitisation runs async
   *     upstream; on any 2xx we treat it as "started" and refresh the
   *     section — the extracted values land shortly after.
   */
  digitisation: {
    documentsForDigitisation: serviceAPI + "/v1/documents/digitisation/{0}",
    triggerDigitisation: serviceAPI + "/v1/lab-report-digitisation/digitise-report",
    // Signed URL token needed to open documents in a new tab — the file
    // host rejects direct hits with 403. Bridge caches the response in
    // localStorage under `zy.bridge.doc.token`; we reuse the same key so
    // one token warms both apps.
    documentToken: baseAPI + "/documents/token/generate",
  },

  /**
   * Lab-test fuzzy-search catalog — federated proxy on the lab investigation
   * service. Response `{data: [{sltCode, ltCode, name, panel, unit, refRange}]}`.
   * unit + refRange are empty for now (api-labtest-service will add them later).
   */
  labtestCatalog: {
    search: labInvestigationV2API + "/api/v1/labtest-catalog?query={0}&limit=20",
  },

  /**
   * Anthropometric v2 (api-anthropometric-v2-service). Tenth v2 service.
   * Fixed 7-parameter set; server auto-writes BMI on Weight/Height saves
   * and WHR on Waist/Hip saves. Flag computed server-side using patient
   * DOB + gender fetched from the monolith (fails hard if unreachable).
   */
  anthropometricV2: {
    list: anthropometricV2API + "/api/v1/anthropometrics/{0}",
    // Summary view — same endpoint with ?view=summary drops statusHistory from
    // the response. Landing-page overview card uses this for the 5-metric strip.
    summary: anthropometricV2API + "/api/v1/anthropometrics/{0}?view=summary",
    add: anthropometricV2API + "/api/v1/anthropometrics/{0}",
  },

  /**
   * Food preferences v2 (api-food-preferences-v2-service). Eleventh v2
   * service. Single-doc-per-patient; frontend debounces chip toggles into
   * a full-doc PUT. Enums come from /config (cached per session).
   * Ingredients proxied to api-recipes-service; allergens reuse Allergy v2.
   */
  foodPreferencesV2: {
    config:      foodPreferencesV2API + "/api/v1/food-preferences/config",
    get:         foodPreferencesV2API + "/api/v1/food-preferences/{0}",
    put:         foodPreferencesV2API + "/api/v1/food-preferences/{0}",
    ingredients: foodPreferencesV2API + "/api/v1/food-preferences/ingredients",
  },
  /**
   * Daily schedule v2 (api-daily-schedule-v2-service). Twelfth v2 service.
   * Timeline + hydration + sleep on one doc; per-entry StatusHistory audit;
   * server-seeded default template on first-load; no-duplicate rule on
   * time AND activity name; soft delete.
   */
  /**
   * Daily schedule v2 — one document per patient PER CALENDAR DAY (mirrors
   * the patient app's routine reporting). {0} = patientId, {1} = entryId,
   * and every day-scoped route takes `?date=YYYY-MM-DD` ({2} on list /
   * mutations, appended by DailyScheduleService). `days` pages the filled
   * days newest-first (`?limit=&before=`); `template` is the default
   * 14-row timeline the UI offers on an unfilled day.
   */
  dailyScheduleV2: {
    template:        dailyScheduleV2API + "/api/v1/daily-schedule/template",
    list:            dailyScheduleV2API + "/api/v1/daily-schedule/{0}?date={1}",
    days:            dailyScheduleV2API + "/api/v1/daily-schedule/{0}/days",
    addEntry:        dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries?date={1}",
    updateEntry:     dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries/{1}?date={2}",
    deleteEntry:     dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries/{1}?date={2}",
    trendEntry:      dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries/{1}/trend",
    batchEntries:    dailyScheduleV2API + "/api/v1/daily-schedule/{0}/entries/batch?date={1}",
    updateHydration: dailyScheduleV2API + "/api/v1/daily-schedule/{0}/hydration?date={1}",
    updateSleep:     dailyScheduleV2API + "/api/v1/daily-schedule/{0}/sleep?date={1}",
  },
  /**
   * AI transcript v2 (api-ai-transcript-v2-service). Thirteenth v2 service.
   * Owns per-call Transcript documents: past-transcripts list, live turn
   * append, AI-captured scratch buffer, per-section Save-and-next commit.
   * All `/captured` writes are consent-gated (400 until ConsentAt is set).
   * `initiate` bundles Exotel outbound call + LiveKit token minting +
   * Transcript creation in one handler.
   */
  aiTranscriptV2: {
    list:              aiTranscriptV2API + "/api/v1/transcripts/{0}",
    start:             aiTranscriptV2API + "/api/v1/transcripts/{0}",
    get:               aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}",
    appendTurn:        aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/turns",
    consent:           aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/consent",
    proposeCaptured:   aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/captured",
    // Phase 3 (ZY-10356) explicit transitions — see reconciliation doc §B.2
    // for the per-endpoint semantics.
    commitCaptured:    aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/captured/{2}/commit",
    revertCaptured:    aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/captured/{2}/revert",
    rejectCaptured:    aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/captured/{2}/reject",
    ackCapturedReopen: aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/captured/{2}/reopen-ack",
    end:               aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/end",
    saveSection:       aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/section/{2}/save",
    initiate:          aiTranscriptV2API + "/api/v1/transcripts/{0}/initiate",
    // Browser-mode: fired by the modal once the CM has joined the LK
    // room and its mic is publishing, to place the patient SIP INVITE.
    dialPatient:       aiTranscriptV2API + "/api/v1/transcripts/{0}/{1}/dial-patient",
  },

  /**
   * SparkTG live-call event feed, served by api-sparktg-wrapper-service.
   * The ongoing-call floater (modules/patient/components/SparkTGCallFloater)
   * subscribes to the SSE stream so a doctor sees a persistent pill while
   * a prescheduled SparkTG call is in flight. Three endpoints:
   *   - streamTicket: POST, header-auth, mints a 60s single-use token.
   *   - stream:       GET SSE, ticket in the query string (EventSource
   *                   cannot send headers). Server emits `event: call`
   *                   with { appointmentId, state, callId, patientName,
   *                   occurredAt }.
   *   - activeCall:   GET, header-auth, point-in-time snapshot used to
   *                   restore the floater after a hard reload.
   * Server 403s non-doctors on ticket + activeCall, so patient sessions
   * silently never see a floater.
   */
  sparktgEvents: {
    streamTicket: serviceAPI + "/v1/sparktg/events/ticket",
    stream:       serviceAPI + "/v1/sparktg/events/stream",
    activeCall:   serviceAPI + "/v1/sparktg/call/active",
  },
};

export default APIConfig;
